<template>
  <div class="call-log-filters-container">
    <!-- Filtro de Data -->
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{ formattedDateRange }}
          </button>
        </template>
      </DatePicker>
    </div>

    <!-- Filtro por Caixa de Entrada -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? $t('CALL_LOG_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedInboxes.length,
                })
              : $t('CALL_LOG_REPORT.FILTERS.INBOX')
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            :placeholder="$t('CALL_LOG_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="inbox in filteredInboxes" :key="inbox.id" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="inbox.id"
                v-model="selectedInboxes"
                @change="applyFilters"
              />
              {{ inbox.name }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro por Tipo -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('type')">
          {{
            selectedTypes.length
              ? $t('CALL_LOG_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedTypes.length,
                })
              : $t('CALL_LOG_REPORT.FILTERS.TYPE')
          }}
        </button>
        <div v-if="openDropdown === 'type'" class="dropdown-content">
          <div v-for="type in availableTypes" :key="type.value" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="type.value"
                v-model="selectedTypes"
                @change="applyFilters"
              />
              {{ type.label }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro por Status -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('status')">
          {{
            selectedStatuses.length
              ? $t('CALL_LOG_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedStatuses.length,
                })
              : $t('CALL_LOG_REPORT.FILTERS.STATUS')
          }}
        </button>
        <div v-if="openDropdown === 'status'" class="dropdown-content">
          <div v-for="status in availableStatuses" :key="status.value" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="status.value"
                v-model="selectedStatuses"
                @change="applyFilters"
              />
              {{ status.label }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro por Duração -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('duration')">
          {{
            selectedDurations.length
              ? $t('CALL_LOG_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedDurations.length,
                })
              : $t('CALL_LOG_REPORT.FILTERS.DURATION')
          }}
        </button>
        <div v-if="openDropdown === 'duration'" class="dropdown-content">
          <div v-for="duration in availableDurations" :key="duration.value" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="duration.value"
                v-model="selectedDurations"
                @change="applyFilters"
              />
              {{ duration.label }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'CallLogFilters',
  components: { 
    DatePicker,
  },
  props: {
    callLogsData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      tempDateRange: [sevenDaysAgo, today],
      customDateRange: [sevenDaysAgo, today],
      openDropdown: null,
      selectedInboxes: [],
      selectedTypes: [],
      selectedStatuses: [],
      selectedDurations: [],
      inboxSearch: '',
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
    }),
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end) {
        return this.$t('CALL_LOG_REPORT.FILTERS.SELECT_DATE');
      }

      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

      return `${format(start)} ~ ${format(end)}`;
    },
    filteredInboxes() {
      return this.inboxes.filter(inbox =>
        inbox.name.toLowerCase().includes(this.inboxSearch.toLowerCase())
      );
    },
    availableInboxes() {
      return this.inboxes.map(inbox => ({
        id: inbox.id,
        name: inbox.name,
      }));
    },
    availableTypes() {
      return [
        { value: 'inbound', label: this.$t('CALL_LOG_REPORT.TYPE.INBOUND') },
        { value: 'outbound', label: this.$t('CALL_LOG_REPORT.TYPE.OUTBOUND') },
      ];
    },
    availableStatuses() {
      return [
        { value: 'completed', label: this.$t('CALL_LOG_REPORT.STATUS.COMPLETED') },
        { value: 'failed', label: this.$t('CALL_LOG_REPORT.STATUS.FAILED') },
      ];
    },
    availableDurations() {
      return [
        { value: 'short', label: this.$t('CALL_LOG_REPORT.DURATION.SHORT') },
        { value: 'medium', label: this.$t('CALL_LOG_REPORT.DURATION.MEDIUM') },
        { value: 'long', label: this.$t('CALL_LOG_REPORT.DURATION.LONG') },
      ];
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;
        this.resetFiltersExceptDate();
        this.applyFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    onDateChange(value) {
      this.customDateRange = value;
      this.applyFilters();
    },
    applyFilters() {
      const filters = {
        customDateRange: this.customDateRange,
        inboxes: this.selectedInboxes,
        types: this.selectedTypes,
        statuses: this.selectedStatuses,
        durations: this.selectedDurations,
      };
      this.$emit('filter-change', filters);
    },
    emitFilters() {
      this.applyFilters();
    },
    resetFiltersExceptDate() {
      this.selectedInboxes = [];
      this.selectedTypes = [];
      this.selectedStatuses = [];
      this.selectedDurations = [];
      this.inboxSearch = '';
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      return date < this.minSelectableDate || date > this.maxSelectableDate;
    },
  },
};
</script>

<style>
.call-log-filters-container {
  width: 280px;
  min-height: 150vh;
  background-color: #000;
  padding: 20px;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  appearance: none;
}

.filter-button:hover {
  background: #1a1a1a;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 8px;
}

.checkbox-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: white;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

::v-deep(.dp__input_icon),
.mx-icon-calendar,
.mx-icon-clear {
  display: none !important;
}
</style>